<template>
  <div class="map-container">
    <div id="map" ref="mapRef" class="map"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// --- Props & Validation ---
const props = defineProps({
  locations: {
    type: [Array, Object],
    required: true,
    validator(value) {
      // Validate Array structure
      if (Array.isArray(value)) {
        return value.every((loc) => 
          typeof loc.latitude === 'number' &&
          typeof loc.longitude === 'number' &&
          typeof loc.timestamp === 'string'
        );
      }
      // Validate Object structure { deviceId: location[] }
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).every((arr) => 
          Array.isArray(arr) && arr.every((loc) =>
            typeof loc.latitude === 'number' &&
            typeof loc.longitude === 'number' &&
            typeof loc.timestamp === 'string'
          )
        );
      }
      return false;
    }
  },
  events: {
    type: Array,
    default: () => []
  }
});

// --- Configuration & Icons ---
const cdn = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/';

const orangeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// --- State Variables ---
const mapRef = ref(null);
let map = null;
let trackLayer = null;
let markersLayer = null;
let eventsLayer = null;
const defaultZoomLevel = 2;

// --- Methods ---
const initializeMap = () => {
  map = L.map(mapRef.value)
    .setView([20, 0], defaultZoomLevel)
    .setMaxBounds([
      [-90, -180],
      [90, 180],
    ]);
  map.options.maxBoundsViscosity = 1.0;

  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    minZoom: 2,
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }).addTo(map);

  if (import.meta.env.PROD) {
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: `${cdn}marker-icon-2x.png`,
      iconUrl: `${cdn}marker-icon.png`,
      shadowUrl: `${cdn}marker-shadow.png`,
    });
  }

  trackLayer = L.layerGroup().addTo(map);
  markersLayer = L.layerGroup().addTo(map);
  eventsLayer = L.layerGroup().addTo(map);
};

const plotMapData = () => {
  // Always clear existing items before rendering new data
  trackLayer.clearLayers();
  markersLayer.clearLayers();
  eventsLayer.clearLayers();

  if (!props.locations) return;

  if (Array.isArray(props.locations)) {
    // Mode A: Single track history layout
    if (!props.locations.length) return;

    const latLngs = props.locations.map((loc) => [loc.latitude, loc.longitude]);
    const polyline = L.polyline(latLngs, {
      color: 'blue',
      weight: 4,
      opacity: 0.7,
    }).addTo(trackLayer);

    props.locations.forEach((loc) => {
      L.marker([loc.latitude, loc.longitude])
        .bindPopup(`<strong>${loc.eventType || 'Location'}</strong><br>${loc.timestamp}`)
        .addTo(markersLayer);
    });

    const newest = props.locations[0];
    L.marker([newest.latitude, newest.longitude], { icon: orangeIcon })
      .bindPopup(`<strong>Latest: ${newest.eventType || ''}</strong><br>${newest.timestamp}`)
      .addTo(markersLayer)
      .setZIndexOffset(1000);

    if (defaultZoomLevel === map.getZoom()) {
      map.fitBounds(polyline.getBounds(), { padding: [50, 50] });
    }

  } else {
    // Mode B: Multiple devices, showing only the latest marker per device
    const bounds = [];

    Object.entries(props.locations).forEach(([deviceId, locArray]) => {
      if (!locArray || !locArray.length) return;

      // Access the newest entry (assumes index 0 matches your track array pattern)
      const newest = locArray[0]; 
      const position = [newest.latitude, newest.longitude];
      bounds.push(position);

      L.marker(position, { icon: orangeIcon })
        .bindPopup(`<strong>Device:</strong> ${deviceId}<br><strong>Event:</strong> ${newest.eventType || 'N/A'}<br>${newest.timestamp}`)
        .addTo(markersLayer)
        .setZIndexOffset(1000);
    });

    if (bounds.length && defaultZoomLevel === map.getZoom()) {
      map.fitBounds(L.latLngBounds(bounds), { padding: [50, 50] });
    }
  }
};

const plotEvents = () => {
  if (!props.events || !props.events.length) return;
  eventsLayer.clearLayers();
  
  props.events.forEach((event) => {
    L.circleMarker([event.latitude, event.longitude], {
      radius: 8,
      color: 'red',
      fillColor: 'red',
      fillOpacity: 0.7,
    })
    .bindPopup(`<strong>Event:</strong> ${event.description} [${event.timestamp}]`)
    .addTo(eventsLayer);
  });
};

// --- Lifecycle & Watchers ---
onMounted(() => {
  initializeMap();
  plotMapData();
  plotEvents();
});

watch(() => props.locations, () => {
  plotMapData();
  plotEvents();
}, { deep: true });

watch(() => props.events, () => {
  plotEvents();
}, { deep: true });
</script>

<style>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.map {
  width: 100%;
  height: 500px;
  border: 1px solid #ccc;
  border-radius: 8px;
}

.leaflet-top {
  z-index: 400;
}
</style>
